import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  createCheckListItem,
  createChecklistToSubcategory,
  deleteChecklistToSubcategory,
  deleteCheckListItem,
  getCheckListItems,
  getChecklistSubcategories,
  getChecklistToSubcategories,
  updateCheckListItem,
} from "../API";
import Loader from "./Loader";
import RateImportWizardModal from "../components/modal/RateImportWizardModal";
import "../assets/styles/manage-checklist.scss";

const CHECKLIST_COLUMNS = [
  { key: "service_number", label: "Service #", type: "number", required: true },
  { key: "description", label: "Description", type: "text", required: true },
  { key: "subcategory_ids", label: "Category", type: "category", separatorAfter: true },
  {
    key: "price_no_modifier",
    label: "No Mod",
    validationLabel: "Price No Mod",
    type: "number",
    group: "price",
  },
  {
    key: "price_12kv_street",
    label: "12kV St",
    validationLabel: "Price 12kV Street",
    type: "number",
    group: "price",
  },
  {
    key: "price_12kv_easement",
    label: "12kV Esmt",
    validationLabel: "Price 12kV Easement",
    type: "number",
    group: "price",
  },
  {
    key: "price_35kv_street",
    label: "35kV St",
    validationLabel: "Price 35kV Street",
    type: "number",
    group: "price",
  },
  {
    key: "price_35kv_easement",
    label: "35kV Esmt",
    validationLabel: "Price 35kV Easement",
    type: "number",
    group: "price",
    separatorAfter: true,
  },
  {
    key: "hours_no_modifier",
    label: "No Mod",
    validationLabel: "Hours No Mod",
    type: "number",
    group: "hours",
  },
  {
    key: "hours_12kv_street",
    label: "12kV St",
    validationLabel: "Hours 12kV Street",
    type: "number",
    group: "hours",
  },
  {
    key: "hours_12kv_easement",
    label: "12kV Esmt",
    validationLabel: "Hours 12kV Easement",
    type: "number",
    group: "hours",
  },
  {
    key: "hours_35kv_street",
    label: "35kV St",
    validationLabel: "Hours 35kV Street",
    type: "number",
    group: "hours",
  },
  {
    key: "hours_35kv_easement",
    label: "35kV Esmt",
    validationLabel: "Hours 35kV Easement",
    type: "number",
    group: "hours",
    separatorAfter: true,
  },
  { key: "is_active", label: "Active", type: "checkbox" },
];

const BASE_COLUMNS = CHECKLIST_COLUMNS.filter(
  (column) => !column.group && column.key !== "is_active"
);
const PRICE_COLUMNS = CHECKLIST_COLUMNS.filter((column) => column.group === "price");
const HOURS_COLUMNS = CHECKLIST_COLUMNS.filter((column) => column.group === "hours");
const ACTIVE_COLUMN = CHECKLIST_COLUMNS.find((column) => column.key === "is_active");
const CATEGORY_COLORS = [
  { bg: "#eaf3ff", border: "#8bbcf4", text: "#1f5f9f" },
  { bg: "#ecfdf3", border: "#86d39f", text: "#247047" },
  { bg: "#fff7e6", border: "#f0bd62", text: "#8a5a14" },
  { bg: "#f3efff", border: "#b8a0f5", text: "#5b3ea6" },
  { bg: "#eef8f7", border: "#76c7bd", text: "#20736b" },
  { bg: "#fff0f4", border: "#ee9bb1", text: "#9a3152" },
  { bg: "#f1f5f9", border: "#94a3b8", text: "#475569" },
];

const EMPTY_VALUES = CHECKLIST_COLUMNS.reduce((values, column) => {
  if (column.type === "checkbox") {
    values[column.key] = true;
  } else if (column.type === "category") {
    values[column.key] = [];
  } else {
    values[column.key] = "";
  }

  return values;
}, {});

const EditIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 20h4.7L19.4 9.3a2 2 0 0 0 0-2.8l-1.9-1.9a2 2 0 0 0-2.8 0L4 15.3V20zM15.9 6l2.1 2.1" />
  </svg>
);

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
  </svg>
);

const SaveIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 6.8" />
  </svg>
);

const CancelIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const resolveRows = (response) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const getSavedChecklistItem = (response) => {
  const payload = response?.data?.data ?? response?.data ?? response;
  return Array.isArray(payload) ? payload[0] : payload;
};

const normalizeSelectedSubcategories = (selectedOptions = []) => {
  const selectionByCategory = new Map();

  selectedOptions.forEach((option) => {
    selectionByCategory.set(option.categoryId, option);
  });

  return Array.from(selectionByCategory.values());
};

const getCategoryColor = (categoryId) => {
  const numericCategoryId = Number(categoryId);
  const colorIndex = Number.isFinite(numericCategoryId)
    ? Math.abs(numericCategoryId) % CATEGORY_COLORS.length
    : 0;

  return CATEGORY_COLORS[colorIndex];
};

const getDraftValues = (item = {}, selectedSubcategoryIds = []) =>
  CHECKLIST_COLUMNS.reduce((values, column) => {
    if (column.type === "checkbox") {
      values[column.key] =
        typeof item[column.key] === "boolean" ? item[column.key] : true;
      return values;
    }

    if (column.type === "category") {
      values[column.key] = selectedSubcategoryIds;
      return values;
    }

    values[column.key] =
      item[column.key] === null || item[column.key] === undefined
        ? ""
        : item[column.key];
    return values;
  }, {});

const isEmptyValue = (value) =>
  value === "" || value === null || value === undefined;

const validateDraft = (values) => {
  if (isEmptyValue(values.service_number)) {
    toast.error("Service number is required");
    return false;
  }

  if (!Number.isFinite(Number(values.service_number))) {
    toast.error("Service number must be a valid number");
    return false;
  }

  if (!String(values.description || "").trim()) {
    toast.error("Description is required");
    return false;
  }

  const invalidNumber = CHECKLIST_COLUMNS.find((column) => {
    if (column.type !== "number" || isEmptyValue(values[column.key])) {
      return false;
    }

    return !Number.isFinite(Number(values[column.key]));
  });

  if (invalidNumber) {
    toast.error(
      `${invalidNumber.validationLabel || invalidNumber.label} must be a valid number`
    );
    return false;
  }

  return true;
};

const buildPayload = (values) =>
  CHECKLIST_COLUMNS.reduce((payload, column) => {
    const rawValue = values[column.key];

    if (column.type === "category") {
      return payload;
    }

    if (column.type === "checkbox") {
      payload[column.key] = Boolean(rawValue);
      return payload;
    }

    if (column.type === "number") {
      payload[column.key] = isEmptyValue(rawValue) ? null : Number(rawValue);
      return payload;
    }

    const trimmedValue = String(rawValue || "").trim();
    payload[column.key] = trimmedValue || null;
    return payload;
  }, {});

const getCellClassName = (column) => {
  const classNames = [];

  if (column.type === "category") {
    classNames.push("checklist-category-cell");
  } else if (column.key === "description") {
    classNames.push("checklist-description-cell");
  } else if (column.type === "number") {
    classNames.push("checklist-number-cell");
  }

  if (column.separatorAfter) {
    classNames.push("checklist-separator-after");
  }

  return classNames.join(" ");
};

const CategoryMultiSelect = ({
  disabled,
  optionById,
  optionGroups,
  onChange,
  selectedIds,
}) => {
  const value = selectedIds
    .map((id) => optionById.get(Number(id)))
    .filter(Boolean);

  return (
    <Select
      className="checklist-category-select"
      classNamePrefix="checklist-category-select"
      closeMenuOnSelect={false}
      components={{
        GroupHeading: ({ data }) => (
          <div
            className="checklist-category-select__custom-heading"
            style={{
              "--category-bg": data.color?.bg,
              "--category-border": data.color?.border,
              "--category-text": data.color?.text,
            }}
          >
            {data.label}
          </div>
        ),
      }}
      hideSelectedOptions={false}
      isClearable
      isDisabled={disabled}
      isMulti
      menuPlacement="auto"
      menuPortalTarget={document.body}
      menuPosition="fixed"
      onChange={(selectedOptions) => {
        const nextOptions = normalizeSelectedSubcategories(selectedOptions || []);
        onChange(nextOptions.map((option) => option.value));
      }}
      options={optionGroups}
      placeholder="Category"
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
          ...base,
          "--category-bg": state.data.color?.bg,
          "--category-border": state.data.color?.border,
          "--category-text": state.data.color?.text,
        }),
      }}
      value={value}
    />
  );
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return value;
};

const ManageChecklist = () => {
  const [checkListItems, setCheckListItems] = useState([]);
  const [checklistSubcategories, setChecklistSubcategories] = useState([]);
  const [checklistToSubcategories, setChecklistToSubcategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [newRowValues, setNewRowValues] = useState({ ...EMPTY_VALUES });
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingRowId, setSavingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [isRateImportOpen, setIsRateImportOpen] = useState(false);
  const rateImportTriggerRef = useRef(null);

  const fetchCheckListData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
    }

    try {
      const [itemsResponse, subcategoriesResponse, mappingsResponse] =
        await Promise.all([
          getCheckListItems(),
          getChecklistSubcategories(),
          getChecklistToSubcategories(),
        ]);

      setCheckListItems(resolveRows(itemsResponse));
      setChecklistSubcategories(resolveRows(subcategoriesResponse));
      setChecklistToSubcategories(resolveRows(mappingsResponse));
    } catch (error) {
      console.error("Error fetching checklist data:", error);
      toast.error("Failed to load checklist data");
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchCheckListData();
  }, [fetchCheckListData]);

  const categoryOptionGroups = useMemo(() => {
    const groupsByCategory = new Map();

    checklistSubcategories.forEach((subcategory) => {
      const category = subcategory?.checklist_category || {};
      const categoryId =
        category?.id ?? subcategory?.checklist_category_id ?? "uncategorized";
      const categoryKey = String(categoryId);

      if (!groupsByCategory.has(categoryKey)) {
        groupsByCategory.set(categoryKey, {
          categoryId: categoryKey,
          color: getCategoryColor(categoryKey),
          categorySortOrder: Number(category?.sort_order ?? 0),
          label: category?.name || "Uncategorized",
          options: [],
        });
      }

      groupsByCategory.get(categoryKey).options.push({
        categoryId: categoryKey,
        categoryName: category?.name || "Uncategorized",
        color: getCategoryColor(categoryKey),
        label: subcategory?.name || `Subcategory ${subcategory?.id}`,
        sortOrder: Number(subcategory?.sort_order ?? 0),
        value: Number(subcategory?.id),
      });
    });

    return Array.from(groupsByCategory.values())
      .sort(
        (firstGroup, secondGroup) =>
          firstGroup.categorySortOrder - secondGroup.categorySortOrder ||
          firstGroup.label.localeCompare(secondGroup.label)
      )
      .map((group) => ({
        color: group.color,
        label: group.label,
        options: group.options.sort(
          (firstOption, secondOption) =>
            firstOption.sortOrder - secondOption.sortOrder ||
            firstOption.label.localeCompare(secondOption.label)
        ),
      }));
  }, [checklistSubcategories]);

  const subcategoryOptionById = useMemo(() => {
    const optionMap = new Map();

    categoryOptionGroups.forEach((group) => {
      group.options.forEach((option) => {
        optionMap.set(Number(option.value), option);
      });
    });

    return optionMap;
  }, [categoryOptionGroups]);

  const mappingsByChecklistId = useMemo(() => {
    const mappingMap = new Map();

    checklistToSubcategories.forEach((mapping) => {
      const checklistId = Number(mapping?.checklist_id);

      if (!Number.isFinite(checklistId)) {
        return;
      }

      if (!mappingMap.has(checklistId)) {
        mappingMap.set(checklistId, []);
      }

      mappingMap.get(checklistId).push(mapping);
    });

    return mappingMap;
  }, [checklistToSubcategories]);

  const getSelectedSubcategoryIds = useCallback(
    (checklistId) =>
      (mappingsByChecklistId.get(Number(checklistId)) || [])
        .map((mapping) => Number(mapping?.checklist_subcategory_id))
        .filter(Number.isFinite),
    [mappingsByChecklistId]
  );

  const getSelectedSubcategoryOptions = useCallback(
    (checklistId) =>
      getSelectedSubcategoryIds(checklistId)
        .map((subcategoryId) => subcategoryOptionById.get(Number(subcategoryId)))
        .filter(Boolean),
    [getSelectedSubcategoryIds, subcategoryOptionById]
  );

  const syncChecklistSubcategories = useCallback(
    async (checklistId, nextSubcategoryIds = []) => {
      const numericChecklistId = Number(checklistId);

      if (!Number.isFinite(numericChecklistId)) {
        return;
      }

      const nextIds = Array.from(
        new Set(
          nextSubcategoryIds.map((subcategoryId) => Number(subcategoryId))
        )
      ).filter(Number.isFinite);
      const currentMappings =
        mappingsByChecklistId.get(numericChecklistId) || [];
      const currentIds = currentMappings
        .map((mapping) => Number(mapping?.checklist_subcategory_id))
        .filter(Number.isFinite);
      const mappingsToDelete = currentMappings.filter(
        (mapping) =>
          mapping?.id &&
          !nextIds.includes(Number(mapping?.checklist_subcategory_id))
      );
      const subcategoryIdsToCreate = nextIds.filter(
        (subcategoryId) => !currentIds.includes(subcategoryId)
      );

      await Promise.all([
        ...mappingsToDelete.map((mapping) =>
          deleteChecklistToSubcategory(mapping.id)
        ),
        ...subcategoryIdsToCreate.map((subcategoryId) =>
          createChecklistToSubcategory({
            checklist_id: numericChecklistId,
            checklist_subcategory_id: subcategoryId,
          })
        ),
      ]);
    },
    [mappingsByChecklistId]
  );

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const sortedItems = [...checkListItems].sort((firstItem, secondItem) => {
      const firstServiceNumber = Number(firstItem?.service_number);
      const secondServiceNumber = Number(secondItem?.service_number);

      if (
        Number.isFinite(firstServiceNumber) &&
        Number.isFinite(secondServiceNumber)
      ) {
        return firstServiceNumber - secondServiceNumber;
      }

      return String(firstItem?.service_number ?? "").localeCompare(
        String(secondItem?.service_number ?? "")
      );
    });

    if (!normalizedSearch) {
      return sortedItems;
    }

    return sortedItems.filter((item) =>
      CHECKLIST_COLUMNS.some((column) => {
        if (column.type === "category") {
          return getSelectedSubcategoryOptions(item.id)
            .map((option) => `${option.categoryName} ${option.label}`)
            .join(" ")
            .toLowerCase()
            .includes(normalizedSearch);
        }

        return String(item[column.key] ?? "")
          .toLowerCase()
          .includes(normalizedSearch);
      })
    );
  }, [checkListItems, getSelectedSubcategoryOptions, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  const pageItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredItems.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredItems, pageSize]);

  const showingStart =
    filteredItems.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingEnd = Math.min(currentPage * pageSize, filteredItems.length);

  const updateDraftValue = (setter, key, value) => {
    setter((previousValues) => ({
      ...previousValues,
      [key]: value,
    }));
  };

  const openAddRow = () => {
    setEditingRowId(null);
    setEditValues({});
    setNewRowValues({ ...EMPTY_VALUES });
    setIsAdding(true);
    setCurrentPage(1);
  };

  const cancelAddRow = () => {
    setIsAdding(false);
    setNewRowValues({ ...EMPTY_VALUES });
  };

  const saveNewRow = async () => {
    if (!validateDraft(newRowValues)) {
      return;
    }

    setSavingRowId("new");
    try {
      const response = await createCheckListItem(buildPayload(newRowValues));
      if ([200, 201].includes(response?.status)) {
        const savedChecklistItem = getSavedChecklistItem(response);
        const selectedSubcategoryIds = newRowValues.subcategory_ids || [];

        if (savedChecklistItem?.id) {
          await syncChecklistSubcategories(
            savedChecklistItem.id,
            selectedSubcategoryIds
          );
        } else if (selectedSubcategoryIds.length > 0) {
          toast.warning(
            "Checklist item added, but categories could not be saved"
          );
        }

        toast.success("Checklist item added");
        setIsAdding(false);
        setNewRowValues({ ...EMPTY_VALUES });
        await fetchCheckListData({ silent: true });
      }
    } catch (error) {
      console.error("Error adding checklist item:", error);
      toast.error("Failed to add checklist item");
    } finally {
      setSavingRowId(null);
    }
  };

  const startEditing = (item) => {
    setIsAdding(false);
    setNewRowValues({ ...EMPTY_VALUES });
    setEditingRowId(item.id);
    setEditValues(getDraftValues(item, getSelectedSubcategoryIds(item.id)));
  };

  const cancelEditing = () => {
    setEditingRowId(null);
    setEditValues({});
  };

  const saveEditedRow = async (item) => {
    if (!validateDraft(editValues)) {
      return;
    }

    setSavingRowId(item.id);
    try {
      const response = await updateCheckListItem(item.id, buildPayload(editValues));
      if ([200, 201, 204].includes(response?.status)) {
        await syncChecklistSubcategories(item.id, editValues.subcategory_ids || []);
        toast.success("Checklist item updated");
        setEditingRowId(null);
        setEditValues({});
        await fetchCheckListData({ silent: true });
      }
    } catch (error) {
      console.error("Error updating checklist item:", error);
      toast.error("Failed to update checklist item");
    } finally {
      setSavingRowId(null);
    }
  };

  const deleteRow = async (item) => {
    const shouldDelete = window.confirm(
      `Delete checklist item ${item.service_number || item.id}?`
    );

    if (!shouldDelete) {
      return;
    }

    setDeletingRowId(item.id);
    try {
      const response = await deleteCheckListItem(item.id);
      if ([200, 202, 204].includes(response?.status)) {
        toast.success("Checklist item deleted");
        setCheckListItems((items) =>
          items.filter((checkListItem) => checkListItem.id !== item.id)
        );
        setChecklistToSubcategories((mappings) =>
          mappings.filter(
            (mapping) => Number(mapping?.checklist_id) !== Number(item.id)
          )
        );
        if (editingRowId === item.id) {
          cancelEditing();
        }
      }
    } catch (error) {
      console.error("Error deleting checklist item:", error);
      toast.error("Failed to delete checklist item");
    } finally {
      setDeletingRowId(null);
    }
  };

  const renderEditableCell = (column, values, setter, rowKey, isSaving) => {
    if (column.type === "category") {
      return (
        <td key={column.key} className={getCellClassName(column)}>
          <CategoryMultiSelect
            disabled={isSaving}
            onChange={(subcategoryIds) =>
              updateDraftValue(setter, column.key, subcategoryIds)
            }
            optionById={subcategoryOptionById}
            optionGroups={categoryOptionGroups}
            selectedIds={values[column.key] || []}
          />
        </td>
      );
    }

    if (column.type === "checkbox") {
      return (
        <td key={column.key} className="checklist-active-edit-cell">
          <label className="checklist-active-toggle">
            <input
              type="checkbox"
              checked={Boolean(values[column.key])}
              onChange={(event) =>
                updateDraftValue(setter, column.key, event.target.checked)
              }
              disabled={isSaving}
              aria-label={`${column.label} ${rowKey}`}
            />
            <span>{values[column.key] ? "Active" : "Inactive"}</span>
          </label>
        </td>
      );
    }

    return (
      <td key={column.key} className={getCellClassName(column)}>
        <input
          type={column.type}
          step={column.type === "number" ? "any" : undefined}
          className="form-control checklist-inline-input"
          value={values[column.key] ?? ""}
          onChange={(event) =>
            updateDraftValue(setter, column.key, event.target.value)
          }
          disabled={isSaving}
          aria-label={`${column.label} ${rowKey}`}
          required={column.required}
        />
      </td>
    );
  };

  const renderReadOnlyCell = (column, item) => {
    if (column.type === "category") {
      const selectedOptions = [...getSelectedSubcategoryOptions(item.id)].sort(
        (firstOption, secondOption) =>
          firstOption.categoryName.localeCompare(secondOption.categoryName) ||
          firstOption.label.localeCompare(secondOption.label)
      );

      return (
        <td key={column.key} className={getCellClassName(column)}>
          {selectedOptions.length > 0 ? (
            <div className="checklist-category-tags">
              {selectedOptions.map((option) => (
                <span
                  key={option.value}
                  className="checklist-category-tag"
                  style={{
                    "--category-bg": option.color?.bg,
                    "--category-border": option.color?.border,
                    "--category-text": option.color?.text,
                  }}
                  title={`${option.categoryName}: ${option.label}`}
                >
                  <span className="checklist-category-tag__category">
                    {option.categoryName}
                  </span>
                  <span className="checklist-category-tag__subcategory">
                    {option.label}
                  </span>
                </span>
              ))}
            </div>
          ) : (
            "-"
          )}
        </td>
      );
    }

    if (column.type === "checkbox") {
      return (
        <td
          key={column.key}
          className={`checklist-active-cell ${
            column.separatorAfter ? "checklist-separator-after" : ""
          }`}
        >
          <span
            className={`status-pill ${
              item[column.key] ? "status-pill-active" : "status-pill-inactive"
            }`}
          >
            {item[column.key] ? "Active" : "Inactive"}
          </span>
        </td>
      );
    }

    return (
      <td key={column.key} className={getCellClassName(column)}>
        {formatValue(item[column.key])}
      </td>
    );
  };

  return (
    <div className="manage-checklist-page project-details-page">
      <div className="project-details-toolbar manage-checklist-toolbar">
        <h1 className="title project-details-title">Manage Checklist</h1>
        <div className="project-details-toolbar__actions manage-checklist-actions">
          <div className="search-container">
            <form
              className="form-wo"
              onSubmit={(event) => event.preventDefault()}
            >
              <button type="button" aria-label="Search checklist">
                <svg
                  width="17"
                  height="16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M7.667 12.667A5.333 5.333 0 107.667 2a5.333 5.333 0 000 10.667zM14.334 14l-2.9-2.9"
                    stroke="currentColor"
                    strokeWidth="1.333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </button>
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              <button
                className="reset"
                type="reset"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </form>
          </div>

          <select
            className="form-select manage-checklist-page-size"
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
            aria-label="Rows per page"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <button
            type="button"
            className="primary-btn manage-checklist-import-rates-btn"
            ref={rateImportTriggerRef}
            onClick={() => setIsRateImportOpen(true)}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 16V4m0 0 4 4m-4-4-4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
              />
            </svg>
            <span>Import rates</span>
          </button>

          <button
            type="button"
            className="primary-btn manage-checklist-add-btn"
            onClick={openAddRow}
            disabled={isAdding || savingRowId !== null}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                d="M12 5v14M5 12h14"
              />
            </svg>
            <span>Add Checklist</span>
          </button>
        </div>
      </div>

      <RateImportWizardModal
        isOpen={isRateImportOpen}
        onClose={() => setIsRateImportOpen(false)}
        onCompleted={() => fetchCheckListData({ silent: true })}
        triggerRef={rateImportTriggerRef}
      />

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="table-responsive data-table-shell manage-checklist-table-shell">
            <table className="table table-striped tableV2 manage-checklist-table">
              <colgroup>
                {CHECKLIST_COLUMNS.map((column) => (
                  <col
                    key={column.key}
                    className={`manage-checklist-col manage-checklist-col-${column.key}`}
                  />
                ))}
                <col className="manage-checklist-col manage-checklist-col-action" />
              </colgroup>
              <thead>
                <tr>
                  {BASE_COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className={column.separatorAfter ? "checklist-separator-after" : ""}
                      scope="col"
                      rowSpan="2"
                    >
                      {column.label}
                    </th>
                  ))}
                  <th
                    className="checklist-separator-after"
                    scope="colgroup"
                    colSpan={PRICE_COLUMNS.length}
                  >
                    Price
                  </th>
                  <th
                    className="checklist-separator-after"
                    scope="colgroup"
                    colSpan={HOURS_COLUMNS.length}
                  >
                    Hours
                  </th>
                  <th key={ACTIVE_COLUMN.key} scope="col" rowSpan="2">
                    {ACTIVE_COLUMN.label}
                  </th>
                  <th
                    scope="col"
                    rowSpan="2"
                    className="manage-checklist-action-heading"
                  >
                    Action
                  </th>
                </tr>
                <tr>
                  {[...PRICE_COLUMNS, ...HOURS_COLUMNS].map((column) => (
                    <th
                      key={column.key}
                      className={column.separatorAfter ? "checklist-separator-after" : ""}
                      scope="col"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isAdding && (
                  <tr className="manage-checklist-editing-row">
                    {CHECKLIST_COLUMNS.map((column) =>
                      renderEditableCell(
                        column,
                        newRowValues,
                        setNewRowValues,
                        "new checklist item",
                        savingRowId === "new"
                      )
                    )}
                    <td className="table-actions manage-checklist-row-actions">
                      <button
                        type="button"
                        className="manage-checklist-icon-btn icon-save"
                        onClick={saveNewRow}
                        disabled={savingRowId === "new"}
                        title="Save"
                        aria-label="Save new checklist item"
                      >
                        <SaveIcon />
                      </button>
                      <button
                        type="button"
                        className="manage-checklist-icon-btn icon-cancel"
                        onClick={cancelAddRow}
                        disabled={savingRowId === "new"}
                        title="Cancel"
                        aria-label="Cancel new checklist item"
                      >
                        <CancelIcon />
                      </button>
                    </td>
                  </tr>
                )}

                {pageItems.length > 0 ? (
                  pageItems.map((item) => {
                    const isEditing = editingRowId === item.id;
                    const isSaving = savingRowId === item.id;
                    const isDeleting = deletingRowId === item.id;

                    return (
                      <tr
                        key={item.id}
                        className={
                          isEditing ? "manage-checklist-editing-row" : undefined
                        }
                      >
                        {CHECKLIST_COLUMNS.map((column) =>
                          isEditing
                            ? renderEditableCell(
                                column,
                                editValues,
                                setEditValues,
                                `checklist item ${item.id}`,
                                isSaving
                              )
                            : renderReadOnlyCell(column, item)
                        )}
                        <td className="table-actions manage-checklist-row-actions">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className="manage-checklist-icon-btn icon-save"
                                onClick={() => saveEditedRow(item)}
                                disabled={isSaving}
                                title="Save"
                                aria-label={`Save checklist item ${item.id}`}
                              >
                                <SaveIcon />
                              </button>
                              <button
                                type="button"
                                className="manage-checklist-icon-btn icon-cancel"
                                onClick={cancelEditing}
                                disabled={isSaving}
                                title="Cancel"
                                aria-label={`Cancel editing checklist item ${item.id}`}
                              >
                                <CancelIcon />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="manage-checklist-icon-btn icon-edit"
                                onClick={() => startEditing(item)}
                                disabled={savingRowId !== null || isDeleting}
                                title="Edit"
                                aria-label={`Edit checklist item ${item.id}`}
                              >
                                <EditIcon />
                              </button>
                              <button
                                type="button"
                                className="manage-checklist-icon-btn icon-delete"
                                onClick={() => deleteRow(item)}
                                disabled={savingRowId !== null || isDeleting}
                                title={isDeleting ? "Deleting" : "Delete"}
                                aria-label={`Delete checklist item ${item.id}`}
                              >
                                <DeleteIcon />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  !isAdding && (
                    <tr>
                      <td
                        colSpan={CHECKLIST_COLUMNS.length + 1}
                        className="manage-checklist-empty"
                      >
                        No checklist items found
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          <div className="manage-checklist-footer">
            <span>
              Showing {showingStart} to {showingEnd} of {filteredItems.length}
            </span>
            <div className="manage-checklist-pagination">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManageChecklist;
